// Phase RV-5 — Upcoming Picks Board (premium editorial refactor).
//
// ROLLOVER Intelligence Enhancement v1.0 §11.1 (Table 26 + 27 + 28).
//
// Doctrine carry-forward:
//   - N16 absolute — empty / pre-publication state ALWAYS renders Countdown.
//     Never blank.
//   - Promotion-not-restriction — no "Sorry" / "Not allowed" / "Locked
//     content". Free users see dots + teasers + "Unlock with Pro" CTAs.
//   - Anon-friendly — the route lives in AuthGate.PUBLIC_ROUTES.
//   - No fabrication — empty rows render the next-publication countdown.
//
// UI: mirrors THE DESK's premium card system (desk-premium.css, --sz-* tokens).
// The SELECTED legs (the published slip) lead each tier as prominent cards;
// EVALUATED runners-up follow (capped, with a "+N more" tail); pure pipeline
// noise (no-model-signal "TBD vs TBD" rejects) is summarised as a count, not a
// wall of empty rows — so users can actually SEE where the picks are.
//
// Architecture: this file exports
//   - `UpcomingPicksBoard` — wires the live hooks (useUpcomingBoard + useUserTier)
//   - `UpcomingPicksBoardView` — pure props-driven view, used by tests
//   - `addToFlexDraft(candidate)` — small localStorage stub for "Add to FLEX".

import type { CSSProperties } from "react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Countdown } from "../super-acca";
import {
  type BoardCandidate,
  type WindowLabel,
  formatCandidateGate,
  formatWindowDate,
  groupByTier,
  nextPublicationTime,
} from "../../rollover/upcomingBoardHelpers.ts";
import { type RolloverTier, getTierSpec } from "../../rollover/tierConfig.ts";
import { useUpcomingBoard } from "../../rollover/useUpcomingBoard.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import "../../styles/desk-premium.css";

const FLEX_DRAFT_KEY = "strikza_flex_draft_v1";

const TIER_ORDER: RolloverTier[] = ["2x", "3x", "4x", "5x", "6x"];

// Rows shown per tier before the "+N more considered" tail. Selected legs are
// always shown in full; the cap only trims the evaluated/assessed runners-up.
const TIER_ROW_CAP = 12;

// Assessed rejects that teach the user nothing — fixtures the model never
// priced. Suppressed from the row list (counted, not rendered).
const NOISE_DISCARDS = new Set([
  "no_model_signal",
  "insufficient_intelligence",
  "outside_window",
]);

const TIER_FILTER_OPTIONS: { id: TierFilter; label: string }[] = [
  { id: "all", label: "ALL" },
  { id: "2x", label: "2×" },
  { id: "3x", label: "3×" },
  { id: "4x", label: "4×" },
  { id: "5x", label: "5×" },
  { id: "6x", label: "6×" },
];

const STATUS_FILTER_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "ALL" },
  { id: "selected", label: "SELECTED" },
  { id: "evaluated", label: "EVALUATED" },
];

export type TierFilter = "all" | RolloverTier;
export type StatusFilter = "all" | "selected" | "evaluated";

/**
 * Idempotent localStorage push for the FLEX draft (key strikza_flex_draft_v1).
 * The FLEX builder (/rollover/flex → useFlexBuilder) reads this same key,
 * resolves the ids against the live board pool, and locks via flex_lock_slip.
 * No-op when the candidate is already in the draft.
 */
export function addToFlexDraft(candidate: BoardCandidate): string[] {
  if (typeof window === "undefined") return [];
  let arr: string[] = [];
  try {
    const raw = window.localStorage.getItem(FLEX_DRAFT_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        arr = parsed.filter((v): v is string => typeof v === "string");
      }
    }
  } catch {
    arr = [];
  }
  if (!arr.includes(candidate.candidate_id)) {
    arr.push(candidate.candidate_id);
  }
  try {
    window.localStorage.setItem(FLEX_DRAFT_KEY, JSON.stringify(arr));
  } catch {
    /* localStorage may be unavailable; the click is best-effort */
  }
  return arr;
}

function parseTierFilter(v: string | null): TierFilter {
  if (v === "2x" || v === "3x" || v === "4x" || v === "5x" || v === "6x") return v;
  return "all";
}
function parseStatusFilter(v: string | null): StatusFilter {
  if (v === "selected" || v === "evaluated") return v;
  return "all";
}

/** A row is pure pipeline noise when the model never priced the fixture. */
function isNoise(c: BoardCandidate): boolean {
  if (!c.match_name || c.match_name === "TBD vs TBD") return true;
  if (c.pool_tag === "assessed" && c.discard_reason && NOISE_DISCARDS.has(c.discard_reason)) {
    return true;
  }
  return false;
}

// ─── Premium style tokens (mirror THE DESK) ──────────────────────────────

const sectionEyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs, 0.7rem)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const filterRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const filterLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  color: "var(--sz-text-disabled)",
  textTransform: "uppercase",
  minWidth: 44,
};

// ─── Pure presentational view (test surface) ─────────────────────────────

export interface UpcomingPicksBoardViewProps {
  candidates: BoardCandidate[];
  windowLabel: WindowLabel | null;
  publishedAt: Date | null;
  firstKickoffAt: Date | null;
  isLive: boolean;
  loading: boolean;
  isPro: boolean;
  tierFilter: TierFilter;
  statusFilter: StatusFilter;
  onTierFilterChange: (next: TierFilter) => void;
  onStatusFilterChange: (next: StatusFilter) => void;
  now?: Date;
  /** Override the "now" used for next-publication countdown (tests). */
  countdownNow?: Date;
  style?: CSSProperties;
}

function FilterPill({
  active,
  label,
  onClick,
  testid,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  testid?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`desk-prem-pill${active ? " tier-maximum" : ""}`}
      style={{
        cursor: "pointer",
        background: active ? "var(--sz-amber-bg, rgba(245,180,60,0.06))" : "transparent",
      }}
    >
      {label}
    </button>
  );
}

function StatusBadge({ pool, isLive, kickoffPassed }: {
  pool: BoardCandidate["pool_tag"];
  isLive: boolean;
  kickoffPassed: boolean;
}): JSX.Element {
  if (isLive && kickoffPassed) {
    return <span className="desk-prem-pill tier-maximum">LIVE</span>;
  }
  if (pool === "selected") {
    return <span className="desk-prem-pill tier-maximum">SELECTED</span>;
  }
  if (pool === "evaluated") {
    return <span className="desk-prem-pill">EVALUATED</span>;
  }
  return <span className="desk-prem-pill tier-read">CONSIDERED</span>;
}

function ConfidenceDots({ filled }: { filled: number }): JSX.Element {
  return (
    <span style={{ display: "inline-flex", gap: 4 }} data-testid="confidence-dots">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: i < filled ? "var(--sz-amber)" : "var(--sz-border-default)",
          }}
        />
      ))}
    </span>
  );
}

/** Highlight the picked side inside a "Home vs Away" match string. */
function MatchHero({ matchName, pick }: { matchName: string; pick: string | null }): JSX.Element {
  const parts = matchName.split(" vs ");
  const home = parts[0];
  const away = parts[1];
  if (parts.length !== 2 || !home || !away) return <span>{matchName}</span>;
  const side = (pick ?? "").toLowerCase();
  const homePicked = side === "home" || side === home.toLowerCase();
  const awayPicked = side === "away" || side === away.toLowerCase();
  return (
    <span>
      <span className={homePicked ? "pickside" : undefined}>{home}</span>
      <span style={{ opacity: 0.5 }}> vs </span>
      <span className={awayPicked ? "pickside" : undefined}>{away}</span>
    </span>
  );
}

function cardTone(pool: BoardCandidate["pool_tag"]): string {
  if (pool === "selected") return " tier-high";
  if (pool === "assessed") return " tier-read";
  return "";
}

function CandidateCard({
  candidate,
  isPro,
  isLive,
  now,
}: {
  candidate: BoardCandidate;
  isPro: boolean;
  isLive: boolean;
  now: Date;
}): JSX.Element {
  const gate = formatCandidateGate(candidate, isPro ? "pro" : "free");
  const kickoff = candidate.kickoff_at ? new Date(candidate.kickoff_at) : null;
  const kickoffPassed = kickoff ? now.getTime() >= kickoff.getTime() : false;
  const confPct =
    candidate.confidence_score !== null && Number.isFinite(candidate.confidence_score)
      ? Math.max(0, Math.min(100, candidate.confidence_score))
      : 0;
  const ctxBits = [
    candidate.league,
    kickoff
      ? `${kickoff.toLocaleString("en-GB", {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Africa/Lagos",
        })} WAT`
      : null,
  ].filter(Boolean);

  return (
    <article className={`desk-prem-card${cardTone(candidate.pool_tag)}`}>
      <div className="desk-prem-context" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span>{candidate.tier} ROLLOVER</span>
        <StatusBadge pool={candidate.pool_tag} isLive={isLive} kickoffPassed={kickoffPassed} />
      </div>

      <div className="desk-prem-match">
        <MatchHero matchName={candidate.match_name} pick={candidate.pick} />
      </div>
      {ctxBits.length > 0 && <div className="desk-prem-context">{ctxBits.join(" · ")}</div>}

      {(candidate.market || candidate.pick) && (
        <div className="desk-prem-pickline">
          {candidate.market ?? "Match Result"}
          {candidate.pick ? <> · <b>{candidate.pick}</b></> : null}
        </div>
      )}

      <div className="desk-prem-metrics">
        {candidate.odds !== null && (
          <span className="desk-prem-odds">{candidate.odds.toFixed(2)}</span>
        )}
        <span className="desk-prem-metric">
          <span className="lbl">Confidence</span>
          {gate.confidence.kind === "dots" ? (
            <ConfidenceDots filled={gate.confidence.dots} />
          ) : (
            <span
              data-testid="confidence-score"
              style={{ fontFamily: "var(--sz-font-mono)", fontSize: "0.8rem", color: "var(--sz-text-secondary)" }}
            >
              {`${Math.round(gate.confidence.score)}/100`}
            </span>
          )}
        </span>
        {gate.confidence.kind === "score" && (
          <span className="desk-prem-confbar"><span style={{ width: `${confPct}%` }} /></span>
        )}
        {gate.clv?.kind === "full" ? (
          <span className="desk-prem-edge">{`+${gate.clv.edge_pct.toFixed(0)}% edge`}</span>
        ) : gate.clv?.kind === "badge" ? (
          <span className="desk-prem-edge">EDGE DETECTED</span>
        ) : null}
      </div>

      {gate.reasoning.kind === "full" && gate.reasoning.full ? (
        <div className="desk-prem-reason" data-testid="reasoning-full">
          {gate.reasoning.full}
        </div>
      ) : null}
      {gate.reasoning.kind === "teaser" && gate.reasoning.teaser ? (
        <div className="desk-prem-reason" data-testid="reasoning-teaser" style={{ color: "var(--sz-text-muted)" }}>
          {gate.reasoning.teaser}…{" "}
          <Link to="/auth?intent=trial" style={{ color: "var(--sz-amber)" }}>
            🔒 Unlock with Pro
          </Link>
        </div>
      ) : null}

      {gate.rejection_reason?.kind === "full" ? (
        <div className="desk-prem-context" data-testid="rejection-full">{gate.rejection_reason.full}</div>
      ) : null}
      {gate.rejection_reason?.kind === "label" ? (
        <div className="desk-prem-context" data-testid="rejection-label">{gate.rejection_reason.label}</div>
      ) : null}

      <div>
        <button
          type="button"
          data-testid={`add-flex-${candidate.candidate_id}`}
          onClick={() => addToFlexDraft(candidate)}
          className="desk-prem-pill tier-maximum"
          style={{ cursor: "pointer", background: "var(--sz-amber-bg, rgba(245,180,60,0.06))" }}
        >
          + Add to FLEX
        </button>
      </div>
    </article>
  );
}

function EmptyStatePanel({
  reason,
  nextAt,
  nextLabel,
}: {
  reason: "between-window" | "scanning";
  nextAt: Date;
  nextLabel: WindowLabel;
}): JSX.Element {
  return (
    <div
      data-testid="board-empty-state"
      className="desk-prem-card"
      style={{ gap: 14 }}
    >
      <div style={sectionEyebrow}>
        {reason === "between-window" ? "BETWEEN WINDOWS" : "PIPELINE SCANNING"}
      </div>
      <div className="desk-prem-match">
        {reason === "between-window"
          ? "Next board publishes soon."
          : "Pipeline scanning — Stage 2 results pending."}
      </div>
      <Countdown targetISO={nextAt.toISOString()} />
      <div style={sectionEyebrow}>
        Next: {nextLabel} ·{" "}
        {nextAt.toLocaleString("en-GB", {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Africa/Lagos",
        })}{" "}
        WAT
      </div>
    </div>
  );
}

export function UpcomingPicksBoardView(
  props: UpcomingPicksBoardViewProps,
): JSX.Element {
  const {
    candidates,
    windowLabel,
    publishedAt,
    isLive,
    loading,
    isPro,
    tierFilter,
    statusFilter,
    onTierFilterChange,
    onStatusFilterChange,
    countdownNow,
    style,
  } = props;
  const propsNow = props.now;
  const now = useMemo(() => propsNow ?? new Date(), [propsNow]);

  // Drop pure pipeline noise (un-priced "TBD vs TBD" rejects), apply the
  // tier / status filters, then collapse duplicates.
  const filtered = useMemo<BoardCandidate[]>(() => {
    const passed = candidates.filter((c) => {
      if (isNoise(c)) return false;
      if (tierFilter !== "all" && c.tier !== tierFilter) return false;
      if (statusFilter === "selected" && c.pool_tag !== "selected") return false;
      if (statusFilter === "evaluated" && c.pool_tag !== "evaluated") return false;
      return true;
    });
    // Defense-in-depth dedup: the same fixture can appear many times when
    // pipeline runs accrue in rollover_candidates. Collapse by
    // tier+fixture+market+pick, keeping the strongest row (selected >
    // evaluated > assessed, then highest confidence). The pipeline also
    // replaces the window pool per run, but this keeps the board honest even
    // if stale rows linger.
    const rank = (p: BoardCandidate["pool_tag"]) =>
      p === "selected" ? 3 : p === "evaluated" ? 2 : 1;
    const best = new Map<string, BoardCandidate>();
    for (const c of passed) {
      const key = `${c.tier}|${c.fixture_id ?? c.match_name}|${c.market ?? ""}|${c.pick ?? ""}`;
      const prev = best.get(key);
      if (
        !prev ||
        rank(c.pool_tag) > rank(prev.pool_tag) ||
        (rank(c.pool_tag) === rank(prev.pool_tag) &&
          (c.confidence_score ?? 0) > (prev.confidence_score ?? 0))
      ) {
        best.set(key, c);
      }
    }
    return [...best.values()];
  }, [candidates, tierFilter, statusFilter]);

  const selectedTotal = useMemo(
    () => filtered.filter((c) => c.pool_tag === "selected").length,
    [filtered],
  );

  const nextPub = useMemo(() => nextPublicationTime(countdownNow ?? now), [countdownNow, now]);

  // N16 — between windows: render countdown.
  if (windowLabel === null) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, ...style }}>
        <div style={sectionEyebrow}>UPCOMING PICKS · BETWEEN WINDOWS</div>
        <EmptyStatePanel reason="between-window" nextAt={nextPub.at} nextLabel={nextPub.windowLabel} />
      </div>
    );
  }

  // N16 — pipeline scanning (window active but no rows yet).
  if (!loading && candidates.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, ...style }}>
        <div style={sectionEyebrow}>
          UPCOMING PICKS · {windowLabel.toUpperCase()} ·{" "}
          {publishedAt ? formatWindowDate(windowLabel, publishedAt) : ""}
        </div>
        <EmptyStatePanel reason="scanning" nextAt={nextPub.at} nextLabel={nextPub.windowLabel} />
      </div>
    );
  }

  const groups = groupByTier(filtered);
  const tiersToRender: RolloverTier[] = tierFilter === "all" ? TIER_ORDER : [tierFilter];

  return (
    <div
      data-testid="upcoming-picks-board"
      style={{ display: "flex", flexDirection: "column", gap: 24, ...style }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={sectionEyebrow}>
          UPCOMING PICKS · {windowLabel.toUpperCase()} ·{" "}
          {publishedAt ? formatWindowDate(windowLabel, publishedAt) : ""}
        </div>
        <div style={{ ...sectionEyebrow, color: "var(--sz-text-disabled)" }}>
          {selectedTotal} selected · {filtered.length} in pool
          {isLive ? " · LIVE — kickoff in progress" : ""}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={filterRow}>
          <span style={filterLabel}>Tier</span>
          {TIER_FILTER_OPTIONS.map((opt) => (
            <FilterPill
              key={opt.id}
              active={tierFilter === opt.id}
              label={opt.label}
              onClick={() => onTierFilterChange(opt.id)}
              testid={`board-tier-filter-${opt.id}`}
            />
          ))}
        </div>
        <div style={filterRow}>
          <span style={filterLabel}>Status</span>
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <FilterPill
              key={opt.id}
              active={statusFilter === opt.id}
              label={opt.label}
              onClick={() => onStatusFilterChange(opt.id)}
              testid={`board-status-filter-${opt.id}`}
            />
          ))}
        </div>
      </div>

      {tiersToRender.map((tier) => {
        const tierRows = groups[tier];
        const selected = tierRows.filter((c) => c.pool_tag === "selected");
        const others = tierRows.filter((c) => c.pool_tag !== "selected");
        const otherSlots = Math.max(0, TIER_ROW_CAP - selected.length);
        const shownOthers = others.slice(0, otherSlots);
        const hiddenCount = others.length - shownOthers.length;
        const shown = [...selected, ...shownOthers];
        const spec = getTierSpec(tier);
        return (
          <section
            key={tier}
            data-testid={`board-tier-section-${tier}`}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span className="desk-prem-pill tier-maximum">{tier}</span>
              <span style={sectionEyebrow}>
                Target {spec.combined_odds_target.toFixed(2)} · {selected.length} selected
                {tierRows.length > selected.length ? ` · ${tierRows.length} in pool` : ""}
              </span>
            </div>
            {shown.length === 0 ? (
              <div
                data-testid={`board-tier-empty-${tier}`}
                className="desk-prem-abstain"
              >
                <span className="reason">
                  No qualifying slip for {tier} this window — agents are still building the pool.
                </span>
              </div>
            ) : (
              <>
                <div className="desk-prem-grid">
                  {shown.map((c) => (
                    <CandidateCard
                      key={c.candidate_id}
                      candidate={c}
                      isPro={isPro}
                      isLive={isLive}
                      now={now}
                    />
                  ))}
                </div>
                {hiddenCount > 0 && (
                  <div style={{ ...sectionEyebrow, color: "var(--sz-text-disabled)" }}>
                    + {hiddenCount} more considered
                  </div>
                )}
              </>
            )}
          </section>
        );
      })}

      <div
        data-testid="board-flex-cta"
        style={{
          position: "sticky",
          bottom: 0,
          paddingTop: 16,
          background: "linear-gradient(to top, var(--sz-bg-base, #0a0a0b) 60%, transparent)",
        }}
      >
        <Link to="/rollover/flex" style={{ textDecoration: "none" }}>
          <span
            className="desk-prem-pill tier-maximum"
            style={{ display: "inline-block", padding: "8px 16px", fontSize: "0.72rem", background: "var(--sz-amber-bg, rgba(245,180,60,0.08))" }}
          >
            Build your FLEX slip from these picks →
          </span>
        </Link>
      </div>
    </div>
  );
}

// ─── Hook-wired wrapper ─────────────────────────────────────────────────

export interface UpcomingPicksBoardProps {
  /** Optional override — defaults to URL `?window=`. */
  windowFilter?: WindowLabel | null;
  style?: CSSProperties;
}

export function UpcomingPicksBoard({
  windowFilter,
  style,
}: UpcomingPicksBoardProps = {}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tierFilter = parseTierFilter(searchParams.get("tier"));
  const statusFilter = parseStatusFilter(searchParams.get("status"));

  const board = useUpcomingBoard({ windowFilter: windowFilter ?? null });
  const { isPro } = useUserTier();

  const setTier = (next: TierFilter) => {
    const sp = new URLSearchParams(searchParams);
    if (next === "all") sp.delete("tier");
    else sp.set("tier", next);
    setSearchParams(sp, { replace: true });
  };
  const setStatus = (next: StatusFilter) => {
    const sp = new URLSearchParams(searchParams);
    if (next === "all") sp.delete("status");
    else sp.set("status", next);
    setSearchParams(sp, { replace: true });
  };

  return (
    <UpcomingPicksBoardView
      candidates={board.candidates}
      windowLabel={board.windowLabel}
      publishedAt={board.publishedAt}
      firstKickoffAt={board.firstKickoffAt}
      isLive={board.isLive}
      loading={board.loading}
      isPro={isPro}
      tierFilter={tierFilter}
      statusFilter={statusFilter}
      onTierFilterChange={setTier}
      onStatusFilterChange={setStatus}
      style={style}
    />
  );
}
